"use client"

import type { ReactNode } from "react"

import { usePartnerAccess } from "@/hooks/use-partner-access"
import { InformationDialog } from "@/components/dialogs"
import { PartnerAccessDisabled, PartnerAccessGate } from "@/components/partners/partner-access-gate"

const MESSAGES = {
  intake_forms: "Intake forms are not available for partner accounts",
  ai_reports: "AI-generated reports are not available for partner accounts",
  scouting: "Scouting is not available for partner accounts",
  dashboards: "Custom dashboards are not available for partner accounts",
  notetaker: "Notetaker is not available for partner accounts",
  engagement: "Engagement is not available for partner accounts",
  prompts: "Prompts are not available for partner accounts",
  email_templates: "Email templates are not available for partner accounts",
  sms_templates: "SMS templates are not available for partner accounts",
  mail_merge: "Mail merge is not available for partner accounts",
  document_filler: "Document filler is not available for partner accounts",
}

type PartnerFeature = keyof typeof MESSAGES

export function PartnerFeatureDisabled({ feature }: { feature: PartnerFeature }) {
  return <PartnerAccessDisabled message={MESSAGES[feature]} />
}

export function PartnerFeatureGate({ feature, children }: { feature: PartnerFeature; children: ReactNode }) {
  return (
    <PartnerAccessGate capability="full_product_access" disabledMessage={MESSAGES[feature]}>
      {children}
    </PartnerAccessGate>
  )
}

export function PartnerFeatureDialogGate({
  feature,
  title,
  open,
  onOpenChange,
  children,
}: {
  feature: PartnerFeature
  title: string
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}) {
  const { enabled, isLoading } = usePartnerAccess("full_product_access")
  if (isLoading) return null
  if (enabled) return children
  return (
    <InformationDialog title={title} open={open} onOpenChange={onOpenChange}>
      <p role="status" className="text-muted-foreground text-sm">
        {MESSAGES[feature]}
      </p>
    </InformationDialog>
  )
}
